import classNames from "classnames";

import Link from "next/link";

import ApplicationLayout from "components/ApplicationLayout";
import Navbar from "components/Navbar";
import PageContent from "components/PageContent";
import Container from "components/Container";
import SharedFooter from "components/SharedFooter";
import { Page } from "types/page";

import ReadyForTakeOff from "components/ReadyForTakeOff";
import SocialFooter from "components/SocialFooter";
import PricingFAQ from "components/pricing/PricingFAQ";

import CheckIcon from "assets/icons/checkmark.svg";
import MinusIcon from "assets/icons/minus.svg";

import AdminToolsIcon from "assets/pricing/admin-tools.svg";
import DeveloperToolsIcon from "assets/pricing/developer-tools.svg";
import ExtensionsIcon from "assets/pricing/extensions.svg";
import QuicklinksIcon from "assets/pricing/quicklinks.svg";
import ScriptCommandsIcon from "assets/pricing/script-commands.svg";
import SharedPrivateExtensionsIcon from "assets/pricing/shared-private-extensions.svg";
import SharedQuicklinksIcon from "assets/pricing/shared-quicklinks.svg";
import SharedSnippetsIcon from "assets/pricing/shared-snippets.svg";
import SnippetsIcon from "assets/pricing/snippets.svg";
import TeamMembersIcon from "assets/pricing/team-members.svg";
import ProgressHalfIcon from "assets/pricing/progress-half.svg";

import Button from "components/Button";

import SendDownloadLink from "components/SendDownloadLink";

import styles from "styles/pricing.module.css";

const Pricing: Page = () => {
  return (
    <>
      <div className={styles.pricingLayout}>
        <h1>Pricing</h1>
        <p className={styles.subtitle}>Increase your teams productivity. Get things done in rapid time.</p>

        <div className={styles.pricingGrid}>
          <h3 className={styles.col1}>Features</h3>
          <div className={styles.col1}>
            <p className={styles.subtle}>Everything you need to take your productivity to the next level.</p>
          </div>
          <div className={styles.col1}></div>
          <div className={classNames(styles.col1, styles.featureDescription)}>
            <QuicklinksIcon />
            Quicklinks
          </div>
          <div className={classNames(styles.col1, styles.featureDescription)}>
            <SharedQuicklinksIcon />
            Shared Quicklinks
          </div>
          <hr className={styles.col1} />
          <div className={classNames(styles.col1, styles.featureDescription)}>
            <SnippetsIcon />
            Snippets
          </div>
          <div className={classNames(styles.col1, styles.featureDescription)}>
            <SharedSnippetsIcon />
            Shared Snippets
          </div>

          <hr className={styles.col1} />

          <div className={classNames(styles.col1, styles.featureDescription)}>
            <ScriptCommandsIcon />
            Script Commands
          </div>

          <hr className={styles.col1} />

          <div className={classNames(styles.col1, styles.featureDescription)}>
            <DeveloperToolsIcon />
            Developer Tools
          </div>
          <div className={classNames(styles.col1, styles.featureDescription)}>
            <ExtensionsIcon />
            Extensions
          </div>
          <div className={classNames(styles.col1, styles.featureDescription)}>
            <SharedPrivateExtensionsIcon />
            Shared Private Extensions
          </div>

          <hr className={styles.col1} />

          <div className={classNames(styles.col1, styles.featureDescription)}>
            <TeamMembersIcon />
            Team Members
          </div>
          <div className={classNames(styles.col1, styles.featureDescription)}>
            <AdminToolsIcon />
            Admin Tools
          </div>

          <h3 className={styles.col2}>Personal</h3>
          <div className={styles.col2}>
            <div className={classNames(styles.pricing, styles.subtle)}>$0 per month</div>
            <p>Unleash your superpowers at no cost. Yes, really. It&apos;s free.</p>
          </div>
          <div className={styles.col2}>
            <div className={styles.mobile}>
              <SendDownloadLink />
            </div>
            <Link href="https://api.raycast.app/v2/download" passHref>
              <Button className={classNames(styles.desktop)} renderAs="a">
                Download for Mac
              </Button>
            </Link>
          </div>
          <div className={classNames(styles.col2, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Quicklinks</span>
          </div>
          <div className={classNames(styles.col2, styles.minus)}>
            <MinusIcon className="icon" />
          </div>

          <hr className={styles.col2} />

          <div className={classNames(styles.col2, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Snippets</span>
          </div>
          <div className={classNames(styles.col2, styles.minus)}>
            <MinusIcon className="icon" />
          </div>

          <hr className={styles.col2} />

          <div className={classNames(styles.col2, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Script Commands</span>
          </div>

          <hr className={styles.col2} />

          <div className={classNames(styles.col2, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Developer Tools</span>
          </div>
          <div className={classNames(styles.col2, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Extensions</span>
          </div>
          <div className={classNames(styles.col2, styles.minus)}>
            <MinusIcon className="icon" />
          </div>

          <hr className={styles.col2} />

          <div className={classNames(styles.col2, styles.minus)}>
            <MinusIcon className="icon" />
          </div>
          <div className={classNames(styles.col2, styles.minus)}>
            <MinusIcon className="icon" />
          </div>

          <h3 className={styles.col3}>
            Teams
            <span className={styles.soon}>Basic</span>
          </h3>
          <div className={styles.col3}>
            <div className={classNames(styles.pricing, styles.subtle)}>$0 per user per month</div>
            <p>Power up your team speed and processes.</p>
          </div>
          <div className={styles.col3}>
            <div className={styles.mobile}>
              <SendDownloadLink />
            </div>
            <Link href="/teams/redirect" passHref>
              <Button className={styles.desktop} renderAs="a">
                Create Organization
              </Button>
            </Link>
          </div>
          <div className={classNames(styles.col3, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Quicklinks</span>
          </div>
          <div className={classNames(styles.col3, styles.check)}>
            <span className={styles.mobile}>
              <ProgressHalfIcon />
            </span>
            <span>
              Up to 30 <span className={styles.mobile}>Shared Quicklinks</span>
            </span>
          </div>

          <hr className={styles.col3} />

          <div className={classNames(styles.col3, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Snippets</span>
          </div>
          <div className={classNames(styles.col3, styles.check)}>
            <span className={styles.mobile}>
              <ProgressHalfIcon />
            </span>
            <span>
              Up to 30 <span className={styles.mobile}>Shared Snippets</span>
            </span>
          </div>

          <hr className={styles.col3} />

          <div className={classNames(styles.col3, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Script Commands</span>
          </div>

          <hr className={styles.col3} />

          <div className={classNames(styles.col3, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Developer Tools</span>
          </div>
          <div className={classNames(styles.col3, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Extensions</span>
          </div>
          <div className={classNames(styles.col3, styles.check)}>
            <span className={styles.mobile}>
              <ProgressHalfIcon />
            </span>
            <span>
              Up to 5 <span className={styles.mobile}>Shared </span>Commands
            </span>
          </div>

          <hr className={styles.col3} />

          <div className={classNames(styles.col3, styles.check)}>
            <span className={styles.mobile}>
              <CheckIcon className="icon" />
            </span>
            <span>
              Unlimited <span className={styles.mobile}>Team Members</span>
            </span>
          </div>
          <div className={classNames(styles.col3, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Admin Tools</span>
          </div>

          <h3 className={styles.col4}>
            Teams
            <span className={styles.paid}>Paid</span>
          </h3>
          <div className={styles.col4}>
            <div className={classNames(styles.pricing, styles.subtle)}>$10 per user per month</div>
            <p>Supercharge your team and maximize productivity.</p>
          </div>
          <div className={styles.col4}>
            <div className={styles.mobile}>
              <SendDownloadLink />
            </div>
            <Link href="/teams/redirect" passHref>
              <Button className={styles.desktop} renderAs="a">
                Create Organization
              </Button>
            </Link>
          </div>
          <div className={classNames(styles.col4, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Quicklinks</span>
          </div>
          <div className={classNames(styles.col4, styles.check)}>
            <span className={styles.mobile}>
              <CheckIcon className="icon" />
            </span>
            <span>
              Unlimited <span className={styles.mobile}>Shared Quicklinks</span>
            </span>
          </div>

          <hr className={styles.col4} />

          <div className={classNames(styles.col4, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Snippets</span>
          </div>
          <div className={classNames(styles.col4, styles.check)}>
            <span className={styles.mobile}>
              <CheckIcon className="icon" />
            </span>
            <span>
              Unlimited <span className={styles.mobile}>Shared Snippets</span>
            </span>
          </div>

          <hr className={styles.col4} />

          <div className={classNames(styles.col4, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Script Commands</span>
          </div>

          <hr className={styles.col4} />

          <div className={classNames(styles.col4, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Developer Tools</span>
          </div>
          <div className={classNames(styles.col4, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Extensions</span>
          </div>
          <div className={classNames(styles.col4, styles.check)}>
            <span className="desktop">Unlimited</span>
            <span className={styles.mobile}>
              <CheckIcon className="icon" />
            </span>
            <span className={styles.mobile}>Shared Private Extensions</span>
          </div>

          <hr className={styles.col4} />

          <div className={classNames(styles.col4, styles.check)}>
            <span className={styles.mobile}>
              <CheckIcon className="icon" />
            </span>
            <span>
              Unlimited <span className={styles.mobile}>Team Members</span>
            </span>
          </div>
          <div className={classNames(styles.col4, styles.check)}>
            <CheckIcon className="icon" />
            <span className={styles.mobile}>Admin Tools</span>
          </div>
        </div>
      </div>
      <PricingFAQ />
      <ReadyForTakeOff />
      <SocialFooter />
    </>
  );
};

Pricing.getLayout = (page) => {
  return (
    <ApplicationLayout>
      <Navbar />
      <PageContent>
        <Container>{page}</Container>
      </PageContent>
      <SharedFooter />
    </ApplicationLayout>
  );
};

export default Pricing;
